import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';
// Services
import { showToast, showLoadingToast } from '../utils/ToastsUtil';
import { countPendingCreations, isNetworkError, readListFromSnapshot, readListSnapshotFirst, saveListToSnapshot, withPendingListItems } from '../utils/offline/OfflineRead';
import { isOnline } from '../utils/offline/Connectivity';
import { canUseFieldWrite, submitFieldWrite } from '../utils/offline/FieldWrite';
import { forgetLocalTags } from '../utils/offline/handlers/TagHandlers';
import { applyLinkChanges } from '../utils/offline/handlers/LinkHandlers';
import i18n from '../locales/i18n';
import DatesUtil from '../utils/DatesUtil';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class ProjectsService {
    static addFolder(newFolder) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/folders/', newFolder, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            let folder = JSON.parse(JSON.stringify(response.data.folder));
            return { ...response.data, folder };
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static updateFolder(folder, ownerId) {
        return Axios.put(BACKEND_ENDPOINT + 'projects/folders/', { folder, ownerId }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then((response) => {
            showToast('folder_updated');
            return response.data;
        }, () => {
            showToast('folder_update_failed');
            return;
        });
    }

    static addProject(newProject) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/', newProject, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('project_added');
            return response.data || {};
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static updateBaseProjects(baseProjects) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/projects/', baseProjects, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('base_projects_updated');
            return response.status;
        }, () => {
            showToast('base_projects_update_failed');
            return;
        });
    }

    static joinProject(baseProjectId) {
        return Axios.post(BACKEND_ENDPOINT + 'admin/projects/', baseProjectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            },
        }).then(response => {
            return response.data;
        }, () => {
            showToast('collaborators_addition_failed');
            return;
        });
    }

    static leaveProject(baseProjectId) {
        return Axios.delete(BACKEND_ENDPOINT + 'admin/projects/' + baseProjectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            },
        }).then(response => {
            return response.data;
        }, () => {
            return;
        });
    }

    static getCollaborators(baseProjectId) {
        return Axios.get(BACKEND_ENDPOINT + 'admin/projects/collaborators/' + baseProjectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            },
        }).then(response => {
            return response.data;
        }, () => {
            return;
        });
    }

    static addCollaborators(data) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/collaborators/', data, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (response.data.invitedUsers?.length > 0) showToast('collaborators_invited');
            if (response.data.addedUserBaseProjects?.length > 0) showToast('collaborators_added');
            return response.data;
        }, () => {
            showToast('collaborators_addition_failed');
            return;
        });
    }

    static addCollaboratorWithInvitation(invitationId, userId) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/collaborators/withInvitation/?invitationId=' + invitationId + '&userId=' + userId).then(response => {
            showToast('collaborator_added');
            return response.data;
        }, error => {
            showToast('connection_failed');
            return;
        });
    }

    static removeCollaborator(projectId, collaboratorId) {
        return Axios.delete(BACKEND_ENDPOINT + 'projects/collaborators/?baseProjectId=' + projectId + '&collaboratorId=' + collaboratorId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('collaborator_removed');
            return response;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static sendInvitation(projectId, email) {
        return Axios.get(`${BACKEND_ENDPOINT}projects/collaborators/sendInvitation/${projectId}/${email}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('invitation_sent');
            return response.data;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static removeInvitation(projectId, email) {
        return Axios.delete(BACKEND_ENDPOINT + 'projects/invitations/?baseProjectId=' + projectId + '&email=' + email, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('invitation_removed');
            return response;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static preloadUserBaseProjects(sort, config = {}) {
        const api = (!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url))
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT);

        return Axios.get(`${api}projects/preloadUserBaseProjects/${sort}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }, ...config
        }).then(response => {
            return response.data;
        }, () => {
            showToast('projects_loading_failed');
            return;
        });
    }

    static getUserRootBaseProjects(surroundings, sort, nbToTake = -1, nbToSkip = 0, config = {}) {
        const api = (!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url))
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT);
        return Axios.get(`${api}projects/userRootBaseProjects/${surroundings}/${sort}/${nbToTake}/${nbToSkip}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }, ...config
        }).then(response => {
            return response.data;
        }, () => {
            return;
        });
    }

    static getUserSubBaseProjects(folderIds, surroundings, config = {}) {
        if (!Array.isArray(folderIds)) folderIds = [folderIds];

        const api = (!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url))
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT);
        return Axios.post(
            `${api}projects/userSubBaseProjects/${surroundings}`,
            folderIds,
            {
                headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
                ...config
            }
        ).then(response => response.data, () => []);
    }

    static getUserBaseProjectsSurroundings() {
        return Axios.get(BACKEND_ENDPOINT + 'projects/userBaseProjectsSurroundings', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            showToast('surroundings_loading_failed');
            return;
        });
    }

    static getNbProjects() {
        return Axios.get(`${BACKEND_ENDPOINT}admin/projects/getNbProjects`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            return;
        });
    }

    static getAllProjects(nbToTake, nbToSkip = 0) {
        return Axios.get(`${BACKEND_ENDPOINT}admin/projects/getAllProjects/${nbToTake}/${nbToSkip}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            showToast('projects_loading_failed');
            return;
        });
    }

    static getProject(id) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, (error) => {
            if (!new Cookies().get('token') && error?.response?.status === 401) showToast('project_login_required');
            else showToast('project_loading_failed');
            return;
        });
    }

    static getFolder(id) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/folder/' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, (error) => {
            if (!new Cookies().get('token') && error?.response?.status === 401) showToast('project_login_required');
            else showToast('project_loading_failed');
            return;
        });
    }

    static getOrganizationBaseProjects() {
        return Axios.get(BACKEND_ENDPOINT + 'projects/organizationBaseProjects', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, error => {
            return;
        });
    }

    static getFolderElements(folderId, shouldLoadSurroundings) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/folders/elements/' + folderId + '/' + shouldLoadSurroundings, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            showToast('projects_loading_failed');
            return;
        });
    }

    static updateProject(project, removedArea, ownerId) {

        const promise = Axios.put(BACKEND_ENDPOINT + 'projects/', { project, removedArea, ownerId }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        });

        showLoadingToast('project_updating', 'project_updated', 'project_update_failed', promise);
        return promise;
    }

    static updateNotifications(rootFolderId, baseProject, recurrence) {
        return Axios.put(BACKEND_ENDPOINT + `projects/notifications/?baseProjectId=${baseProject.id}&recurrence=${recurrence}`, null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('notifications_status_updated');
            return response.status;
        }, () => {
            showToast('notifications_status_update_failed');
            return;
        });
    }

    static toggleFavorite(rootFolderId, baseProject, state) {
        return Axios.put(BACKEND_ENDPOINT + `projects/toggleFavorite/${rootFolderId}/${baseProject.id}`, null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast(state ? 'favorite_status_added' : 'favorite_status_removed', baseProject.type === 'folder' ? 'dossier' : 'projet');
            return response.status;
        }, () => {
            showToast('favorite_status_update_failed');
            return;
        });
    }

    static changeUserRoles(baseProjectId, ubpToModify) {
        return Axios.put(BACKEND_ENDPOINT + 'projects/changeUserRoles/' + baseProjectId, ubpToModify, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(() => {
            showToast('roles_updated');
            return true;
        }, () => {
            showToast('roles_update_failed');
            return false;
        });
    }

    static deleteBaseProject(baseProjectId, type, isDeletion) {
        return Axios.delete(BACKEND_ENDPOINT + 'projects/' + baseProjectId + '/' + type, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(() => {
            const action = isDeletion ? 'deleted' : 'left';
            showToast(type === 'project' ? `project_${action}` : `folder_${action}`);
            return true;
        }, (error) => {
            if (isOnline() && error?.response) {
                if (error.response.data === 'Project is importing data') showToast('import_in_progress');
                else {
                    const action = isDeletion ? 'deletion' : 'leaving';
                    showToast(type === 'project' ? `project_${action}_failed` : `folder_${action}_failed`);
                }
                return false;
            }
            else return true;
        });
    }

    static moveBaseProjects(baseProjectIds, folderId, type) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/' + folderId, baseProjectIds, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (baseProjectIds.length === 1) showToast(type === 'folder' ? 'folder_moved' : 'project_moved');
            else showToast('base_projects_moved');
            return response.data;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static updateFieldCategories(project) {
        return Axios.put(BACKEND_ENDPOINT + 'projects/fieldCategories/' + project.id, project.fieldCategories || [], {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            showToast('project_update_failed');
            return;
        });
    }

    static async getStationElements(projectId, { fallback = true } = {}) {
        const fromSnapshot = await readListSnapshotFirst(projectId, 'stationElements');
        if (fromSnapshot !== undefined) return fromSnapshot;
        return Axios.get(BACKEND_ENDPOINT + 'projects/stationElements/' + projectId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then((response) => {
            saveListToSnapshot(projectId, 'stationElements', response.data);
            return response.data;
        }, async (error) => {
            if (fallback && isNetworkError(error)) {
                const cached = await readListFromSnapshot(projectId, 'stationElements');
                if (cached !== undefined) return cached;
            }
            showToast('linked_elements_loading_failed');
            return;
        });
    }

    static async getLinkedElements(projectId, { fallback = true } = {}) {
        const fromSnapshot = await readListSnapshotFirst(projectId, 'linkedElements');
        if (fromSnapshot !== undefined) return fromSnapshot;
        return Axios.get(BACKEND_ENDPOINT + 'projects/linkedElements/' + projectId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(async (response) => {
            const data = await withPendingListItems(projectId, 'linkedElements', response.data);
            saveListToSnapshot(projectId, 'linkedElements', data);
            return data;
        }, async (error) => {
            if (fallback && isNetworkError(error)) {
                const cached = await readListFromSnapshot(projectId, 'linkedElements');
                if (cached !== undefined) return cached;
            }
            showToast('linked_elements_loading_failed');
            return;
        });
    }

    // Hors-ligne : ids des éléments créés, modifiés ou supprimés depuis une date, par catégorie
    static getChanges(projectId, since) {
        return Axios.get(`${BACKEND_ENDPOINT}projects/${projectId}/changes?since=${encodeURIComponent(since)}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => response.data, () => null);
    }

    static toggleLinkedElements(linkedElementsToAdd, linkedElementsToDelete, projectId) {
        if (canUseFieldWrite()) return ProjectsService.writeLinksThroughOutbox('link.toggle', projectId, linkedElementsToAdd, linkedElementsToDelete, 'elements_linked');
        return Axios.post(BACKEND_ENDPOINT + 'projects/linkedElements/' + projectId, { linkedElementsToAdd, linkedElementsToDelete }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('elements_linked');
            return response.status;
        }, () => {
            // Sans réseau (hors pilote) : rien n'est gardé, l'appelant n'applique pas la liaison
            showToast(isOnline() ? 'connection_failed' : 'offline_not_saved');
            return;
        });
    }

    static deleteLinkedElements(linkedElements, projectId) {
        if (canUseFieldWrite()) return ProjectsService.writeLinksThroughOutbox('link.delete', projectId, [], linkedElements, 'elements_unlinked');
        return Axios.delete(BACKEND_ENDPOINT + 'projects/linkedElements/' + projectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
            },
            data: linkedElements
        }).then(response => {
            showToast('elements_unlinked');
            return response.status;
        }, () => {
            showToast(isOnline() ? 'connection_failed' : 'offline_not_saved');
            return;
        });
    }

    // Liaisons par la file : elles attendent la création des éléments liés (refs) ; 200 en local, les appelants ne lisent pas la valeur
    static writeLinksThroughOutbox(type, projectId, toAdd, toDelete, successToast) {
        const links = [...toAdd, ...toDelete];
        return submitFieldWrite({
            op: {
                type, projectId, target: { entity: 'links', id: uuidv4() },
                touches: links.map(link => `link:${link.elementId}:${link.linkedElementId}`),
                refs: links.flatMap(link => [{ entity: 'element', id: link.elementId }, { entity: 'element', id: link.linkedElementId }]),
                payload: type === 'link.toggle' ? { toAdd, toDelete } : { linkedElements: toDelete }
            },
            applyLocal: services => services.snapshot.updateList(projectId, 'linkedElements', list => applyLinkChanges(list, toAdd, toDelete)),
            buildLocalData: () => null
        }).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.isLocal ? 200 : response.status;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static getMapAreas() {
        return Axios.get(BACKEND_ENDPOINT + 'projects/mapAreas').then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('municipalities_loading_failed');
            return;
        });
    }

    static getMunicipalities() {
        return Axios.get(BACKEND_ENDPOINT + 'projects/municipalities').then(response => {
            return response.data;
        }, () => {
            showToast('municipalities_loading_failed');
            return;
        });
    }

    static getMunicipality(id) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/municipality/' + id).then(response => {
            return response.data;
        }, () => {
            showToast('municipalities_loading_failed');
            return;
        });
    }

    static getProjections() {
        return Axios.get(BACKEND_ENDPOINT + 'projects/projections/').then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('projections_loading_failed');
            return;
        });
    }

    static getInvitation(invitationId) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/getInvitation/' + invitationId).then(response => {
            return response;
        }, error => {
            return error.response.status;
        });
    }

    static getLogs(id) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/logs/' + id, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, error => {
            return error.response.status;
        });
    }

    static getPointInTime(id, year) {
        return Axios.get(BACKEND_ENDPOINT + `projects/pointInTime/${id}/${year}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, error => {
            return error.response.status;
        });
    }

    static getOldestHistory(projectId) {
        return Axios.get(BACKEND_ENDPOINT + `projects/oldestHistory/${projectId}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, error => {
            return error.response.status;
        });
    }

    // direct : paramètres du projet, qui attendent la liste des tags du projet en retour
    static addProjectTags(projectTags, { direct = false } = {}) {
        if (canUseFieldWrite({ direct }) && projectTags?.length) return ProjectsService.addProjectTagsThroughOutbox(projectTags);
        return Axios.post(BACKEND_ENDPOINT + 'projects/tags/', projectTags, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => { return; });
    }

    // Les tags gardent l'uuid du formulaire ; réponse locale null, les appelants gardent leur liste déjà affichée
    static addProjectTagsThroughOutbox(projectTags) {
        return submitFieldWrite({
            op: {
                type: 'tag.create', projectId: projectTags[0].projectId,
                target: { entity: 'tag', id: projectTags[0].id }, touches: projectTags.map(tag => `tag:${tag.id}`),
                softDependents: true, payload: { tags: projectTags }
            },
            revertLocal: services => forgetLocalTags(services.idMap, projectTags),
            buildLocalData: () => null
        }).then(response => (response.isLocal ? null : response.data), () => null);
    }

    static updateProjectTag(tag) {
        return Axios.put(BACKEND_ENDPOINT + 'projects/tag/', tag, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => { return; });
    }

    static deleteProjectTags(ids) {
        return Axios.delete(BACKEND_ENDPOINT + 'projects/tags/', {
            data: ids,
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return true;
        }, () => { return; });
    }

    static isMaxElementsReached(project, type, text) {
        return Axios.get(BACKEND_ENDPOINT + type + '/isMaxReached/' + project.id, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            saveListToSnapshot(project.id, `isMaxReached:${type}`, response.data);
            return response.data;
        }, async (error) => {
            if (isOnline() && error?.response) {
                if (error.response.data === 'Project is importing data') showToast('import_in_progress');
                else showToast('elements_limit_not_available', text);
                return;
            }
            // Sans réseau : dernier quota connu + éléments créés sur l'appareil et pas encore envoyés (repères non limités)
            const cached = isNetworkError(error) && project ? await readListFromSnapshot(project.id, `isMaxReached:${type}`) : undefined;
            if (cached) {
                const nbElements = cached.nbElements + (type === 'markers' ? 0 : await countPendingCreations());
                return { ...cached, nbElements, state: cached.maxElements !== -1 && nbElements >= cached.maxElements };
            }
            showToast('elements_limit_not_available', text);
            return;
        });
    }

    static updateReferences(projectId, category) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/projects/updateReferences/' + projectId + '/' + category, null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('references_updated');
            return response.data;
        }, () => {
            showToast('references_update_failed');
            return;
        });
    }

    static duplicateProject(project, fieldCategoriesWithFieldsToCreate = [], duplicateElements = true, isRecommendation = false, asAdmin = false) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/duplicate', { project, fieldCategoriesWithFieldsToCreate, duplicateElements, isRecommendation, asAdmin }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, (error) => {
            showToast('project_duplication_failed');
            return Promise.reject(error.response?.data);
        });
    }

    static getInvitations() {
        return Axios.get(BACKEND_ENDPOINT + 'admin/projects/invitations/', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('invitations_loading_failed');
            return;
        });
    }

    static deleteInvitations(ids) {
        return Axios.delete(BACKEND_ENDPOINT + 'admin/projects/invitations/', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids: ids }
        }).then(response => {
            return response.status;
        }, () => {
            showToast(ids.length > 1 ? 'invitations_deletion_failed' : 'invitation_deletion_failed');
            return;
        });
    }

    static exportHistoryAsXLSX(projectLabel, rows) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/exportXLSX/logs/', rows, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${projectLabel} - ${i18n.t("Historique")}.xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static exportProjectsAsExcel(elements) {
        return Axios.post(BACKEND_ENDPOINT + 'admin/projects/exportXLSX/', elements, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${i18n.t("Projets")} - ${DatesUtil.getFormattedLocaleDateString(new Date().toISOString())}.xlsx`);
            document.getElementById('admin-panel__grid').appendChild(link);
            link.click();
            document.getElementById('admin-panel__grid').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (isOnline()) showToast('projects_xlsx_export_failed');
        });
    }

    static shareProject(baseProjectId, searchParams) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/share/', { baseProjectId, searchParams }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => { return; });
    }

    static getBaseProjectSubEntities(baseProjectId, types) {
        return Axios.post(BACKEND_ENDPOINT + 'projects/getBaseProjectSubEntities/' + baseProjectId, types, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => { return; });
    }

    static getCaches(id) {
        return Axios.get((!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url))
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT) + `caches/${id}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, error => {
            return error?.response?.status;
        });
    }

    static importSurroundings(formData, axiosOptions = {}) {
        return Axios.post(`${BACKEND_ENDPOINT}projects/importSurroundings/`, formData, {
            ...axiosOptions,
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'content-type': 'multipart/form-data'
            }
        }).then(response => {
            return response.data;
        }, (error) => {
            if (isOnline() && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return;
        });
    }

    static preloadElements(projectId) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/preload/' + projectId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('preloading_failed');
            return;
        });
    }

    static transferProjectData(transferData) {
        return Axios.post(BACKEND_ENDPOINT + 'admin/projects/transfer/', transferData, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('data_transferred');
            return response.status;
        }, (response) => {
            // if (response.response.data === 'Target organization subscription limits reached') showToast('target_organization_subscription_limits_reached');
            if (isOnline()) showToast('data_transfer_failed');
        });
    }

    static resyncStationElements(projectId) {
        return Axios.post(BACKEND_ENDPOINT + 'admin/projects/' + projectId + '/stationElements/resync', null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const { added, updated, deleted } = response.data;
            showToast('station_elements_resynced', added, updated, deleted);
            return response.data;
        }, () => {
            if (isOnline()) showToast('station_elements_resync_failed');
        });
    }

    static getBaseProjectType(id) {
        return Axios.get(BACKEND_ENDPOINT + 'projects/getBaseProjectType/' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            return;
        });
    }
}